import { StateCreator } from 'zustand'
import { ICameraController } from '../../viewer/cameraController'
import ICameraConstraints from '../../viewer/interfaces/camera-constraints.interface'
import { PointOfView } from '../../../../trillion-common/src/objects/point-of-view.object'
import { CameraConstraints } from '../../../../trillion-common/src/objects/camera-constraints.object'
import { OnViewerConfigSaved } from '../../viewer/types/on-viewer-config-saved'
import { OnWidgetConfigSaved } from '../../viewer/types/on-widget-config-saved'
import { ViewerConfig } from '../../../../trillion-common/src/objects/viewer-config.object'
import { WidgetConfig } from '../../../../trillion-common/src/objects/widget-config.object'
import { LoaderTheme } from '../../viewer/types/loader-theme'
import { JewelTab } from '../../viewer/types/jewel-tab'
import { Bloom } from '../../../../trillion-common/src/objects/posteffects/bloom.object'
import { BloomSelective } from '../../../../trillion-common/src/objects/posteffects/bloom-selective.object'
import { DepthOfField } from '../../../../trillion-common/src/objects/posteffects/depth-of-field.object'
import { SMAA } from '../../../../trillion-common/src/objects/posteffects/smaa.object'
import { SSAO } from '../../../../trillion-common/src/objects/posteffects/ssao.object'
import { N8AO } from '../../../../trillion-common/src/objects/posteffects/n8ao.object'
import { TextureMapping } from '../../../../trillion-common/src/objects/posteffects/texture-mapping.object'
import { ToneMapping } from '../../../../trillion-common/src/objects/posteffects/tone-mapping.object'
import { MaterialColor } from '../../../../trillion-common/src/objects/material-color.object'
import { DiamondCut } from '../../../../trillion-common/src/objects/diamond-cut.object'
import { OnEnvironmentUpload } from '../../viewer/types/on-environment-upload'
import { ReflectionMapDto } from '../../../../trillion-common/src/dto/reflection-map.dto'
import { StoneHalo } from '../../../../trillion-common/src/objects/stone-halo.object'
import { StoneSize } from '../../../../trillion-common/src/objects/stone-size.object'
import { DiamondBand } from '../../../../trillion-common/src/objects/diamond-band.object'

export type TrillionViewerState = {
  defaultCameraConstraints: ICameraConstraints
  isGUIEnabled: boolean
  isShowPerformance: boolean
  cameraController: ICameraController | undefined
  rotateObject: boolean
  rotateSpeed: number | undefined
  isStopped: boolean
  isLoading: boolean
  width: number
  height: number
  zoomMin: number | undefined
  zoomMax: number | undefined
  reducePixelRatio: boolean
  isFreeMoveCameraModeEnabled: boolean
  pointsOfView: PointOfView[]
  currentPointOfViewIndx: number
  onViewerConfigSaved: OnViewerConfigSaved | undefined
  onWidgetConfigSaved: OnWidgetConfigSaved | undefined
  onEnvironmentUpload: OnEnvironmentUpload | undefined
  cameraIsAnimating: boolean
  autoRotateSpeed: number | undefined
  background: string | undefined
  viewerConfig: ViewerConfig | undefined
  widgetConfig: WidgetConfig | undefined
  bloom: Bloom | undefined
  bloomSelective: Omit<BloomSelective, 'selectiveNames'> | undefined
  depthOfField: DepthOfField | undefined
  ssao: SSAO | undefined
  n8ao: N8AO | undefined
  smaa: SMAA | undefined
  textureMapping: TextureMapping | undefined
  toneMapping: ToneMapping | undefined
  posteffectsConfigJson: string | undefined
  startSize: number | undefined
  darkPointsOfView: boolean | undefined
  loaderTheme: LoaderTheme
  currentMaterialColor: MaterialColor | undefined
  currentDiamondCut: DiamondCut | undefined
  currentHalo: StoneHalo | undefined
  currentSize: StoneSize | undefined
  currentBand: DiamondBand | undefined
  currentLetter: string | undefined
  reflectionMap: ReflectionMapDto | undefined
  selectedJewelTab: JewelTab
  engravingText: string | undefined
  tryon: boolean | undefined
}

export type TrillionViewerActions = {
  setDefaultCameraConstraints: (cameraConstraints: ICameraConstraints) => void
  setIsGUIEnabled: (isGUIEnabled: boolean) => void
  setIsPerformanceEnabled: (isPerformanceEnabled: boolean) => void
  setCameraController: (cameraController: ICameraController | undefined) => void
  setRotateObject: (rotateObject: boolean) => void
  setRotateSpeed: (rotationSpeed: number | undefined) => void
  setIsStopped: (isStopped: boolean) => void
  setIsLoading: (isLoading: boolean) => void
  setWidth: (width: number) => void
  setHeight: (height: number) => void
  setZoomMin: (zoomMin: number | undefined) => void
  setZoomMax: (zoomMax: number | undefined) => void
  setReducePixelRatio: (reducePixelRatio: boolean) => void
  setIsFreeMoveCameraModeEnabled: (isFreeMoveCameraModeEnabled: boolean) => void
  setPointsOfView: (pointsOfView: PointOfView[]) => void
  setCurrentPointOfViewIndx: (indx: number) => void
  getCurrentPointOfView: () => PointOfView | undefined
  setOnViewerConfigSaved: (callback: OnViewerConfigSaved | undefined) => void
  setOnWidgetConfigSaved: (callback: OnWidgetConfigSaved | undefined) => void
  setOnEnvironmentUpload: (callback: OnEnvironmentUpload | undefined) => void
  setCameraIsAnimating: (cameraIsAnimating: boolean) => void
  setAutoRotateSpeed: (autoRotateSpeed: number | undefined) => void
  setBackground: (background: string | undefined) => void
  setViewerConfig: (viewerConfig: ViewerConfig | undefined) => void
  setWidgetConfig: (widgetConfig: WidgetConfig | undefined) => void
  setBloom: (bloom: Bloom | undefined) => void
  setBloomSelective: (bloom: Omit<BloomSelective, 'selectiveNames'> | undefined) => void
  setDepthOfField: (depthOfField: DepthOfField | undefined) => void
  setSmaa: (smaa: SMAA | undefined) => void
  setSsao: (ssao: SSAO | undefined) => void
  setN8ao: (n8ao: N8AO | undefined) => void
  setTextureMapping: (textureMapping: TextureMapping | undefined) => void
  setToneMapping: (toneMapping: ToneMapping | undefined) => void
  setPosteffectsConfigJson: (config: string | undefined) => void
  setStartSize: (startSize: number | undefined) => void
  setDarkPointsOfView: (darkPointsOfView: boolean) => void
  setLoaderTheme: (loaderTheme: LoaderTheme) => void
  setCurrentMaterialColor: (newColor: MaterialColor) => void
  setReflectionMap: (reflectionMap: ReflectionMapDto | undefined) => void
  setCurrentDiamondCut: (newCut: DiamondCut) => void
  setCurrentHalo: (newHalo: StoneHalo) => void
  setCurrentSize: (newSize: StoneSize) => void
  setCurrentBand: (newBand: DiamondBand) => void
  setEngravingText: (text: string) => void
  setCurrentLetter: (text: string) => void
  setSelectedJewelTab: (selectedJewelTab: JewelTab) => void
  setTryon: (value: boolean | undefined) => void
}

export type TrillionViewerSlice = TrillionViewerState & TrillionViewerActions

function generateInitialState(): TrillionViewerState {
  return {
    defaultCameraConstraints: new CameraConstraints(),
    isGUIEnabled: false,
    isShowPerformance: false,
    cameraController: undefined,
    rotateObject: false,
    rotateSpeed: undefined,
    isStopped: true,
    isLoading: true,
    width: 0,
    height: 0,
    zoomMin: undefined,
    zoomMax: undefined,
    reducePixelRatio: false,
    isFreeMoveCameraModeEnabled: false,
    pointsOfView: [],
    currentPointOfViewIndx: -1,
    onViewerConfigSaved: undefined,
    onWidgetConfigSaved: undefined,
    onEnvironmentUpload: undefined,
    cameraIsAnimating: false,
    autoRotateSpeed: 0.6,
    background: undefined,
    viewerConfig: undefined,
    widgetConfig: undefined,
    bloom: undefined,
    bloomSelective: undefined,
    depthOfField: undefined,
    smaa: undefined,
    ssao: undefined,
    n8ao: undefined,
    textureMapping: undefined,
    toneMapping: undefined,
    posteffectsConfigJson: undefined,
    startSize: undefined,
    darkPointsOfView: undefined,
    loaderTheme: 'darkOnLight',
    currentMaterialColor: undefined,
    reflectionMap: undefined,
    currentDiamondCut: undefined,
    currentLetter: undefined,
    currentHalo: undefined,
    currentSize: undefined,
    currentBand: undefined,
    engravingText: undefined,
    selectedJewelTab: 'Band Ring',
    tryon: undefined,
  }
}

const initialState = generateInitialState()

export const createTrillionViewerSlice: StateCreator<
  TrillionViewerSlice,
  [],
  [],
  TrillionViewerSlice
> = (set, get) => ({
  ...initialState,
  setDefaultCameraConstraints: (cameraConstraints: ICameraConstraints) => {
    set({ defaultCameraConstraints: cameraConstraints })
  },
  setIsGUIEnabled: (isGUIEnabled: boolean) => {
    set({ isGUIEnabled })
  },
  setIsPerformanceEnabled: (isPerformanceEnabled: boolean) => {
    set({ isShowPerformance: isPerformanceEnabled })
  },
  setCameraController: (cameraController: ICameraController | undefined) => {
    set({ cameraController })
  },
  setRotateObject: (rotateObject: boolean) => {
    set({ rotateObject })
  },
  setRotateSpeed: (rotateSpeed: number | undefined) => {
    set({ rotateSpeed })
  },
  setIsStopped: (isStopped: boolean) => {
    set({ isStopped })
  },
  setIsLoading: (isLoading: boolean) => {
    set({ isLoading })
  },
  setWidth: (width: number) => {
    set({ width })
  },
  setHeight: (height: number) => {
    set({ height })
  },
  setReducePixelRatio: (reducePixelRatio: boolean) => {
    set({ reducePixelRatio })
  },
  setIsFreeMoveCameraModeEnabled: (isFreeMoveCameraModeEnabled: boolean) => {
    set({ isFreeMoveCameraModeEnabled })
  },
  setPointsOfView: (pointsOfView: PointOfView[]) => {
    set({ pointsOfView })
  },
  setCurrentPointOfViewIndx: (indx: number) => {
    set({ currentPointOfViewIndx: indx })
  },
  getCurrentPointOfView: () => {
    const curIndx = get().currentPointOfViewIndx
    if (curIndx === -1) return undefined
    return get().pointsOfView[curIndx]
  },
  setOnViewerConfigSaved: (callback: OnViewerConfigSaved | undefined) => {
    set({ onViewerConfigSaved: callback })
  },
  setOnWidgetConfigSaved: (callback: OnWidgetConfigSaved | undefined) => {
    set({ onWidgetConfigSaved: callback })
  },
  setOnEnvironmentUpload: (callback: OnEnvironmentUpload | undefined) => {
    set({ onEnvironmentUpload: callback })
  },
  setCameraIsAnimating: (cameraIsAnimating: boolean) => {
    set({ cameraIsAnimating })
  },
  setAutoRotateSpeed: (autoRotateSpeed: number | undefined) => {
    set({ autoRotateSpeed })
  },
  setBackground: (background: string | undefined) => {
    set({ background })
  },
  setViewerConfig: (viewerConfig: ViewerConfig | undefined) => {
    set({ viewerConfig })
  },
  setWidgetConfig: (widgetConfig: WidgetConfig | undefined) => {
    set({ widgetConfig })
  },
  setBloom: (bloom: Bloom | undefined) => {
    set({ bloom })
  },
  setBloomSelective: (bloomSelective: Omit<BloomSelective, 'selectiveNames'> | undefined) => {
    set({ bloomSelective })
  },
  setDepthOfField: (depthOfField: DepthOfField | undefined) => {
    set({ depthOfField })
  },
  setSmaa: (smaa: SMAA | undefined) => {
    set({ smaa })
  },
  setSsao: (ssao: SSAO | undefined) => {
    set({ ssao })
  },
  setN8ao: (n8ao: N8AO | undefined) => {
    set({ n8ao })
  },
  setTextureMapping: (textureMapping: TextureMapping | undefined) => {
    set({ textureMapping })
  },
  setToneMapping: (toneMapping: ToneMapping | undefined) => {
    set({ toneMapping })
  },
  setPosteffectsConfigJson: (posteffectsConfigJson: string | undefined) => {
    set({ posteffectsConfigJson })
  },
  setStartSize: (startSize: number | undefined) => {
    set({ startSize })
  },
  setZoomMax: (zoomMax: number | undefined) => {
    set({ zoomMax })
  },
  setZoomMin: (zoomMin: number | undefined) => {
    set({ zoomMin })
  },
  setDarkPointsOfView: (darkPointsOfView: boolean) => {
    set({ darkPointsOfView })
  },
  setLoaderTheme: (loaderTheme: LoaderTheme) => {
    set({ loaderTheme })
  },
  setCurrentMaterialColor: (currentMaterialColor: MaterialColor) => {
    set({ currentMaterialColor })
  },
  setCurrentDiamondCut: (currentDiamondCut: DiamondCut) => {
    set({ currentDiamondCut })
  },
  setCurrentHalo: (currentHalo: StoneHalo) => {
    set({ currentHalo })
  },
  setCurrentSize: (currentSize: StoneSize) => {
    set({ currentSize })
  },
  setCurrentBand: (currentBand: DiamondBand) => {
    set({ currentBand })
  },
  setReflectionMap: (reflectionMap: ReflectionMapDto | undefined) => {
    set({ reflectionMap: reflectionMap })
  },
  setEngravingText: (engravingText: string | undefined) => {
    set({ engravingText })
  },
  setCurrentLetter: (currentLetter: string | undefined) => {
    set({ currentLetter })
  },
  setSelectedJewelTab: (selectedJewelTab: JewelTab | undefined) => {
    set({ selectedJewelTab })
  },
  setTryon: (tryon: boolean | undefined) => {
    set({ tryon })
  },
})
